import React, { useMemo, useState } from "react";
import { Avatar, Button, Dropdown, Empty, Input, List, Modal, Tooltip, Typography, message as antMessage } from "antd";
import {
  DownOutlined,
  LockOutlined,
  MessageOutlined,
  NumberOutlined,
  PlusOutlined,
  PushpinFilled,
  PushpinOutlined,
  RightOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useAccess } from "@umijs/max";
import { useChat } from "./ChatContext";
import ReadReceiptTicks from "./ReadReceiptTicks";
import { resolveAvatarUrl } from "./zulipClient";
import { dmKeyFromUserIds } from "./useConversations";
import { groupAndSortChannels } from "./channelGrouping";
import { CHAT_VIEW_ITEMS } from "./ChatViewPane";
import { CHAT_MODAL_Z_INDEX } from "./types";
import type { ChatViewKind, ZulipStream } from "./types";

// Whether a view's underlying data has anything new since it was last
// looked at. "inbox"/"recent"/"combined" all surface the same unread
// channel+DM messages, just laid out differently, so they share one signal;
// "mentions" has its own count since Zulip tracks it separately (a message
// can be unread without mentioning you). "starred" has no unread concept —
// starring is user-driven, not a read-state — so it never shows the dot.
function viewHasUnread(key: ChatViewKind, unread: { count: number; mentions: number }): boolean {
  if (key === "mentions") return unread.mentions > 0;
  if (key === "starred") return false;
  return unread.count > 0;
}

function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

const COLLAPSED_SECTIONS_KEY = "FR_CHAT_COLLAPSED_SECTIONS";

// Persisted (not just in-memory) for the same reason listCollapsed/peopleOpen
// are in ChatPanel: in widget mode this whole component unmounts whenever a
// thread/view is selected, so plain useState would forget which sections
// were collapsed the moment the user came back.
function readCollapsedSections(): Set<string> {
  try {
    const raw = localStorage.getItem(COLLAPSED_SECTIONS_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch {
    return new Set();
  }
}

function writeCollapsedSections(ids: Set<string>): void {
  try {
    localStorage.setItem(COLLAPSED_SECTIONS_KEY, JSON.stringify([...ids]));
  } catch {
    // ignore
  }
}

const SectionHeader: React.FC<{ id: string; title: string; collapsed: boolean; onToggle: (id: string) => void }> = ({ id, title, collapsed, onToggle }) => (
  <div className="chat-section-header" onClick={() => onToggle(id)}>
    <span className="chat-section-header__chevron">{collapsed ? <RightOutlined /> : <DownOutlined />}</span>
    {title}
  </div>
);

// Deterministic pastel color from a name — used for channel/group-DM avatar
// backgrounds so each one reads as a distinct "space" the way Google Chat's
// colored space icons do, without needing per-channel icon assets.
const AVATAR_PALETTE = ["#1a73e8", "#d93025", "#188038", "#e37400", "#8430ce", "#12805c", "#c5221f", "#0b57d0"];
function colorFor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0;
  return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length];
}

const ConversationList: React.FC = () => {
  const {
    subscriptions,
    browseStreams,
    channelFolders,
    streamTopics,
    refreshBrowseStreams,
    expandedStreamIds,
    toggleStreamExpanded,
    dmConversations,
    selection,
    activeView,
    setActiveView,
    unread,
    selectChannel,
    selectDm,
    unreadForChannelTopic,
    unreadForDm,
    unreadForStream,
    createChannel,
    setNewChannelOpen,
    setNewDmOpen,
    realmUsers,
    myUserId,
    enableReadReceipts,
    readReceipts,
    togglePinned,
  } = useChat();
  const access = useAccess();

  const [search, setSearch] = useState("");
  const [browseOpen, setBrowseOpen] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(readCollapsedSections);

  const toggleSection = (id: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      writeCollapsedSections(next);
      return next;
    });
  };

  const activeViewItem = CHAT_VIEW_ITEMS.find((v) => v.key === activeView);

  const filteredChannels = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return subscriptions;
    return subscriptions.filter((s) => s.name.toLowerCase().includes(q));
  }, [subscriptions, search]);

  const channelSections = useMemo(
    () => groupAndSortChannels(filteredChannels, channelFolders),
    [filteredChannels, channelFolders],
  );

  const filteredDms = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return dmConversations;
    return dmConversations.filter((c) => c.label.toLowerCase().includes(q));
  }, [dmConversations, search]);

  const openBrowse = () => {
    setBrowseOpen(true);
    refreshBrowseStreams();
  };

  const notYetJoined = useMemo(() => {
    const joined = new Set(subscriptions.map((s) => s.stream_id));
    return browseStreams.filter((s) => !joined.has(s.stream_id) && !s.is_archived);
  }, [browseStreams, subscriptions]);

  // Same folder grouping as the joined-channels list above (groupAndSortChannels),
  // reused here so Browse channels reads the same way. The "Pinned Channels"
  // section it can produce never actually appears for an unjoined channel —
  // pin_to_top is per-subscription state Zulip only sets on channels you're
  // already in — so in practice this only ever yields folder sections + "Other".
  const browseSections = useMemo(() => groupAndSortChannels(notYetJoined, channelFolders), [notYetJoined, channelFolders]);

  const handleJoin = async (stream: ZulipStream) => {
    try {
      await createChannel({ name: stream.name, inviteOnly: false, principals: [] });
      antMessage.success(`Joined #${stream.name}`);
    } catch (e: any) {
      antMessage.error(e?.message || "Failed to join channel.");
    }
  };

  const menuItems = [
    { key: "dm", icon: <MessageOutlined />, label: "New direct message", onClick: () => setNewDmOpen(true) },
    // Creating a channel is a "Manage Chat Channels" action — everyone else
    // can still join channels that already exist via Browse.
    ...(access.canManageChatChannels
      ? [{ key: "channel", icon: <TeamOutlined />, label: "New channel", onClick: () => setNewChannelOpen(true) }]
      : []),
    { key: "browse", icon: <NumberOutlined />, label: "Browse channels", onClick: openBrowse },
  ];

  return (
    <div className="chat-conversation-list">
      <div className="chat-conversation-list__header">
        <Input.Search
          placeholder="Search"
          allowClear
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Dropdown menu={{ items: menuItems }} trigger={["click"]} placement="bottomRight">
          <Button type="text" size="small" icon={<PlusOutlined />} />
        </Dropdown>
      </div>

      <div className="chat-conversation-list__scroll">
        {(() => {
          const viewsCompact = collapsedSections.has("views");
          return (
            <>
              <div className="chat-section-header chat-views-header" onClick={() => toggleSection("views")}>
                <span className="chat-section-header__chevron">{viewsCompact ? <RightOutlined /> : <DownOutlined />}</span>
                <span>Views</span>
                {viewsCompact && (
                  <div className="chat-view-toolbar" onClick={(e) => e.stopPropagation()}>
                    {CHAT_VIEW_ITEMS.map((v) => {
                      const hasUnread = viewHasUnread(v.key, unread);
                      const isActive = activeView === v.key;
                      return (
                        <Tooltip title={v.label} key={v.key}>
                          <button
                            type="button"
                            className={`chat-view-toolbar__btn${isActive ? " chat-view-toolbar__btn--active" : ""}`}
                            onClick={() => setActiveView(isActive ? null : v.key)}
                          >
                            {v.icon}
                            {hasUnread && <span className="chat-unread-dot chat-view-toolbar__dot" />}
                          </button>
                        </Tooltip>
                      );
                    })}
                  </div>
                )}
              </div>
              {viewsCompact ? (
                activeViewItem && (
                  <div className="chat-row chat-row--active" onClick={() => setActiveView(null)}>
                    <span className="chat-view-row__icon">{activeViewItem.icon}</span>
                    <div className="chat-row__main">
                      <Typography.Text strong ellipsis className="chat-row__title">
                        {activeViewItem.label}
                      </Typography.Text>
                    </div>
                  </div>
                )
              ) : (
                <List
                  size="small"
                  dataSource={CHAT_VIEW_ITEMS}
                  renderItem={(v) => {
                    const hasUnread = viewHasUnread(v.key, unread);
                    const isActive = activeView === v.key;
                    return (
                      <List.Item
                        className={`chat-row${isActive ? " chat-row--active" : ""}`}
                        onClick={() => setActiveView(isActive ? null : v.key)}
                      >
                        <span className="chat-view-row__icon">{v.icon}</span>
                        <div className="chat-row__main">
                          <Typography.Text strong={isActive || hasUnread} ellipsis className="chat-row__title">
                            {v.label}
                          </Typography.Text>
                        </div>
                        {hasUnread && <span className="chat-unread-dot" />}
                      </List.Item>
                    );
                  }}
                />
              )}
            </>
          );
        })()}

        <SectionHeader id="dms" title="Direct Messages" collapsed={collapsedSections.has("dms")} onToggle={toggleSection} />
        {!collapsedSections.has("dms") && (
          <>
            {filteredDms.length === 0 && (
              <Typography.Text type="secondary" className="chat-conversation-list__empty-hint">
                No conversations yet
              </Typography.Text>
            )}
            <List
              size="small"
              dataSource={filteredDms}
              renderItem={(c) => {
                const isActive = selection?.kind === "dm" && dmKeyFromUserIds(selection.userIds) === c.key;
                const unread = unreadForDm(c.userIds);
                const otherUser = c.userIds.length === 1 ? realmUsers.find((u) => u.user_id === c.userIds[0]) : undefined;
                // Sent/read tick on the last message I sent in this
                // conversation — Zulip only tracks read (not delivered), so
                // there's nothing to show for a message the other side
                // hasn't read yet beyond a single "sent" tick. Read receipts
                // are also a per-account privacy setting on Zulip's side, so
                // a still-single-tick row can simply mean the recipient has
                // that off.
                const lastMine = enableReadReceipts && !!c.lastMessage && c.lastMessage.sender_id === myUserId;
                const otherIds = c.userIds.filter((id) => id !== myUserId);
                const readerIds = lastMine ? readReceipts[c.lastMessage!.id] : undefined;
                const seen = lastMine && !!readerIds && otherIds.length > 0 && otherIds.every((id) => readerIds!.includes(id));
                const showReceipt = lastMine && !!readerIds;
                return (
                  <List.Item
                    className={`chat-row${isActive ? " chat-row--active" : ""}`}
                    onClick={() => selectDm(c.userIds, c.label)}
                  >
                    {c.userIds.length > 1 ? (
                      <Avatar size={36} icon={<TeamOutlined />} style={{ background: colorFor(c.label), flexShrink: 0 }} />
                    ) : (
                      <Avatar size={36} src={resolveAvatarUrl(otherUser?.avatar_url) || undefined} style={{ background: colorFor(c.label), flexShrink: 0 }}>
                        {!otherUser?.avatar_url && c.label.charAt(0).toUpperCase()}
                      </Avatar>
                    )}
                    <div className="chat-row__main">
                      <div className="chat-row__title-line">
                        <Typography.Text strong={unread > 0} ellipsis className="chat-row__title">
                          {c.label}
                        </Typography.Text>
                        {c.lastTimestamp > 0 && (
                          <span className="chat-row__time">{dayjs.unix(c.lastTimestamp).format("M/D h:mm A")}</span>
                        )}
                      </div>
                      {(c.lastMessage || showReceipt) && (
                        <div className="chat-row__preview-line">
                          {showReceipt && <ReadReceiptTicks seen={seen} className="chat-row__receipt" />}
                          {c.lastMessage && (
                            <Typography.Text type="secondary" ellipsis className="chat-row__preview">
                              {stripHtml(c.lastMessage.content)}
                            </Typography.Text>
                          )}
                        </div>
                      )}
                    </div>
                    {unread > 0 && <span className="chat-unread-dot" />}
                  </List.Item>
                );
              }}
            />
          </>
        )}

        {filteredChannels.length === 0 && (
          <>
            <div className="chat-conversation-list__section-title">Channels</div>
            <Typography.Text type="secondary" className="chat-conversation-list__empty-hint">
              No channels yet — create or browse one
            </Typography.Text>
          </>
        )}
        {channelSections.map((section) => {
          const collapsed = collapsedSections.has(section.id);
          return (
            <React.Fragment key={section.id}>
              <SectionHeader id={section.id} title={section.title} collapsed={collapsed} onToggle={toggleSection} />
              {!collapsed && (
                <List
                  size="small"
                  dataSource={section.streams}
                  renderItem={(stream) => {
                    const isOpen = expandedStreamIds.has(stream.stream_id);
                    const topics = streamTopics[stream.stream_id] ?? [];
                    const streamUnread = unreadForStream(stream.stream_id);
                    return (
                      <div key={stream.stream_id}>
                        <List.Item className="chat-row" onClick={() => toggleStreamExpanded(stream.stream_id)}>
                          <span className="chat-row__chevron">
                            {isOpen ? <DownOutlined /> : <RightOutlined />}
                          </span>
                          <Avatar
                            size={36}
                            shape="square"
                            icon={stream.invite_only ? <LockOutlined /> : undefined}
                            style={{ background: colorFor(stream.name), borderRadius: 10, flexShrink: 0 }}
                          >
                            {!stream.invite_only && stream.name.charAt(0).toUpperCase()}
                          </Avatar>
                          <div className="chat-row__main">
                            <Typography.Text strong={streamUnread > 0} ellipsis className="chat-row__title">
                              {stream.name}
                            </Typography.Text>
                          </div>
                          {streamUnread > 0 && <span className="chat-unread-dot" />}
                          <Tooltip title={stream.pin_to_top ? "Unpin channel" : "Pin channel"}>
                            <Button
                              type="text"
                              size="small"
                              className={`chat-row__pin${stream.pin_to_top ? " chat-row__pin--active" : ""}`}
                              icon={stream.pin_to_top ? <PushpinFilled /> : <PushpinOutlined />}
                              onClick={(e) => {
                                e.stopPropagation();
                                togglePinned(stream);
                              }}
                            />
                          </Tooltip>
                          <Tooltip title="New topic">
                            <Button
                              type="text"
                              size="small"
                              className="chat-row__new-topic"
                              icon={<PlusOutlined />}
                              onClick={(e) => {
                                e.stopPropagation();
                                selectChannel(stream, "");
                              }}
                            />
                          </Tooltip>
                        </List.Item>
                        {isOpen && (
                          <div className="chat-topic-list">
                            {topics.length === 0 && (
                              <Typography.Text type="secondary" className="chat-conversation-list__empty-hint">
                                No topics yet — start one
                              </Typography.Text>
                            )}
                            {topics.map((t) => {
                              const isActive = selection?.kind === "channel" && selection.streamId === stream.stream_id && selection.topic === t.name;
                              const unread = unreadForChannelTopic(stream.stream_id, t.name);
                              return (
                                <div
                                  key={t.name}
                                  className={`chat-topic-row${isActive ? " chat-topic-row--active" : ""}`}
                                  onClick={() => selectChannel(stream, t.name)}
                                >
                                  <NumberOutlined className="chat-topic-row__icon" />
                                  <Typography.Text strong={unread > 0} ellipsis className="chat-topic-row__title">
                                    {t.name}
                                  </Typography.Text>
                                  {unread > 0 && <span className="chat-unread-dot" />}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  }}
                />
              )}
            </React.Fragment>
          );
        })}
        {notYetJoined.length > 0 && (
          <div className="chat-browse-more" onClick={openBrowse}>
            <NumberOutlined /> Browse {notYetJoined.length} more channel{notYetJoined.length === 1 ? "" : "s"}
          </div>
        )}
      </div>

      <Modal
        title="Browse channels"
        open={browseOpen}
        onCancel={() => setBrowseOpen(false)}
        footer={null}
        destroyOnClose
        zIndex={CHAT_MODAL_Z_INDEX}
      >
        {notYetJoined.length === 0 ? (
          <Empty description="No other channels to join" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          browseSections.map((section) => (
            <div key={section.id} className="chat-browse-section">
              <div className="chat-browse-section__title">{section.title}</div>
              <List
                dataSource={section.streams}
                renderItem={(stream) => (
                  <List.Item
                    actions={[
                      // Private channels here were surfaced only via the "Manage
                      // Chat Channels" discovery list (see refreshBrowseStreams) —
                      // a member's own Zulip credentials can never self-subscribe
                      // to a private stream they weren't invited to, so there's
                      // no working Join action to offer for them.
                      stream.invite_only ? (
                        <Typography.Text key="private" type="secondary" style={{ fontSize: 12 }}>
                          Ask to be invited
                        </Typography.Text>
                      ) : (
                        <Button key="join" size="small" onClick={() => handleJoin(stream)}>
                          Join
                        </Button>
                      ),
                    ]}
                  >
                    <List.Item.Meta
                      avatar={
                        <Avatar shape="square" style={{ background: colorFor(stream.name), borderRadius: 8 }} icon={stream.invite_only ? <LockOutlined /> : undefined}>
                          {!stream.invite_only && stream.name.charAt(0).toUpperCase()}
                        </Avatar>
                      }
                      title={stream.name}
                      description={stream.description}
                    />
                  </List.Item>
                )}
              />
            </div>
          ))
        )}
      </Modal>
    </div>
  );
};

export default ConversationList;
